import { useNavigate } from "react-router-dom";
import { Package, Edit, Trash2, ArrowLeft, DollarSign, Tag, Layers, Image as ImageIcon, Store, MapPin, Star, Eye, Clock } from "lucide-react";
import PageLayout from "../../../layouts/PageLayout";
import useCustomParams from "../../../hooks/params/useCustomParams";
import { useState } from "react";
import DeleteConfirmationModal from "../../../components/ui/DeleteConfirmationModal";
import { alertSuccess, alertServerError } from "../../../helpers/alertError";
import { useProduct } from "@cabukaka/hooks";

export default function ProductDetailsPage({}) {
  const navigate = useNavigate();
  const { id } = useCustomParams();
  const { product, loading, deleteProduct } = useProduct({ id: id! });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!product?.id) return;
    setIsDeleting(true);
    try {
      await deleteProduct(product.id);
      alertSuccess("Produit supprimé avec succès");
      navigate(-1);
    } catch (error: any) {
      alertServerError(error);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  if (loading) {
    return (
      <PageLayout title="Détails du produit">
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        </div>
      </PageLayout>
    );
  }

  if (!product) {
    return (
      <PageLayout title="Détails du produit">
        <div className="text-center py-20 text-gray-500">Produit non trouvé</div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Détails du produit"
      actions={
        <div className="flex gap-2">
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
          >
            <ArrowLeft size={18} /> Retour
          </button>
          <button 
            onClick={() => navigate(`/admin/products/update?id=${id}`)} 
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            <Edit size={18} /> Modifier
          </button>
          <button 
            onClick={() => setShowDeleteModal(true)} 
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
          >
            <Trash2 size={18} /> Supprimer
          </button>
        </div>
      }
    >
      {/* En-tête avec image */}
      <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
        <div className="flex items-center gap-4">
          {product.mainImage ? (
            <img src={product.mainImage} alt={product.name} className="w-20 h-20 rounded-lg object-cover" />
          ) : (
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center">
              <Package size={40} className="text-primary" />
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold">{product.name}</h1>
            {product.slug && <p className="text-sm text-gray-500 mt-1">slug: {product.slug}</p>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Informations générales */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Layers size={18} className="text-primary" />
            Informations générales
          </h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-500">Nom</span>
              <span className="font-medium">{product.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Catégorie</span>
              <span>{product.category?.name || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Sous-catégorie</span>
              <span>{product.subcategory?.name || '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Mode de prix</span>
              <span className="capitalize">{product.priceMode === 'fixed' ? 'Prix fixe' : 'Variantes'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Unité de vente</span>
              <span className="capitalize">
                {product.unit === 'kg' ? 'Kilogramme' : product.unit === 'liter' ? 'Litre' : 'Pièce'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Statut</span>
              <span className={`px-2 py-1 rounded-full text-xs ${product.inStock ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                {product.inStock ? 'En stock' : 'Rupture'}
              </span>
            </div>
          </div>
        </div>

        {/* Informations financières */}
        <div className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <DollarSign size={18} className="text-primary" />
            Informations financières
          </h2>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-500">Prix original</span>
              <span>{product.originalPrice ? `${parseInt(product.originalPrice).toLocaleString('fr-FR')} FCFA` : '-'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Prix de vente</span>
              <span className="font-bold text-lg text-primary">{product.priceLabel || `${product.price?.toLocaleString('fr-FR')} FCFA`}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Stock disponible</span>
              <span>{product.stock || 0} unités</span>
            </div>
          </div>
        </div>

        {/* Boutique */}
        {product.shop && (
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Store size={18} className="text-primary" />
              Boutique
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-500">Nom</span>
                <span className="font-medium">{product.shop.name}</span>
              </div>
              {product.shop.location?.city && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Ville</span>
                  <span className="flex items-center gap-1">
                    <MapPin size={14} className="text-gray-400" />
                    {product.shop.location.city}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Évaluation */}
        {(product.rating || product.reviewsCount) && (
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Star size={18} className="text-primary" />
              Évaluation
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-500">Note moyenne</span>
                <span className="flex items-center gap-1">
                  <Star size={16} className="fill-amber-400 text-amber-400" />
                  {product.rating || 0} / 5
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Nombre d'avis</span>
                <span>{product.reviewsCount || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Vendus</span>
                <span>{product.soldCount || 0}</span>
              </div>
            </div>
          </div>
        )}

        {/* Description */}
        {product.description && (
          <div className="bg-white rounded-xl shadow-sm border p-6 md:col-span-2">
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Tag size={18} className="text-primary" />
              Description
            </h2>
            <p className="text-gray-600 leading-relaxed">{product.description}</p>
          </div>
        )}

        {/* Images */}
        {product.images && product.images.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border p-6 md:col-span-2">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <ImageIcon size={18} className="text-primary" />
              Images du produit
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {product.images.map((img, idx) => (
                <img key={idx} src={img} alt={`${product.name} - ${idx + 1}`} className="w-full h-32 object-cover rounded-lg border" />
              ))}
            </div>
          </div>
        )}

        {/* Variantes */}
        {product.variants && product.variants.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border p-6 md:col-span-2">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Layers size={18} className="text-primary" />
              Variantes
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b">
                  <tr>
                    <th className="text-left py-2">Nom</th>
                    <th className="text-left py-2">Valeur</th>
                    <th className="text-left py-2">Prix</th>
                    <th className="text-left py-2">Stock</th>
                  </tr>
                </thead>
                <tbody>
                  {product.variants.map((variant, idx) => (
                    <tr key={idx} className="border-b last:border-0">
                      <td className="py-2">{variant.name}</td>
                      <td className="py-2">{variant.value}</td>
                      <td className="py-2">{variant.priceLabel || `${variant.price?.toLocaleString('fr-FR')} FCFA`}</td>
                      <td className="py-2">{variant.stock || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Métadonnées */}
        <div className="bg-white rounded-xl shadow-sm border p-6 md:col-span-2">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Clock size={18} className="text-primary" />
            Métadonnées
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">ID</span>
              <span className="font-mono">{product.id}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Créé le</span>
              <span>{new Date(product.createdAt).toLocaleDateString('fr-FR')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Dernière modification</span>
              <span>{new Date(product.updatedAt).toLocaleDateString('fr-FR')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">ID Boutique</span>
              <span className="font-mono">{product.shopId}</span>
            </div>
          </div>
        </div>
      </div>

      <DeleteConfirmationModal 
        isOpen={showDeleteModal} 
        onClose={() => setShowDeleteModal(false)} 
        onConfirm={handleDelete} 
        title="Supprimer le produit" 
        message={`Supprimer "${product.name}" ?`} 
        confirmText={isDeleting ? "Suppression..." : "Supprimer"} 
      />
    </PageLayout>
  );
}